import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useSiteSettings } from '../services/siteSettingsService';

const Terms = () => {
  const { settings } = useSiteSettings();

  return (
    <>
      <Helmet>
        <title>Conditions Générales - {settings.brandFullName}</title>
        <meta
          name="description"
          content={`Conditions générales de vente et d'utilisation de ${settings.brandFullName}`}
        />
      </Helmet>

      <main className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h1 className="text-3xl font-bold mb-8">Conditions Générales</h1>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Objet</h2>
              <p className="mb-4">
                Les présentes conditions générales régissent les relations entre {settings.brandFullName} et ses clients dans le cadre de la vente de services numériques, de conseil, de création, d&apos;automatisation, de marketing et de développement sur mesure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Prestations et documents contractuels</h2>
              <p className="mb-4">
                Couteau Suisse Digital propose des prestations numériques sur mesure. La nature exacte des services, leur périmètre, les livrables, les délais, le tarif, les modalités de paiement et les éventuelles conditions particulières sont précisés dans le devis, la proposition commerciale, le bon de commande, le lien de paiement ou la facture émise au client.
              </p>
              <p>
                En cas de contradiction entre les présentes conditions générales et un document commercial spécifique accepté par le client, ce dernier prévaut pour la prestation concernée.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. Prix, devise et fiscalité</h2>
              <p className="mb-4">
                Les prix affichés sur le site peuvent être présentés en EUR, CHF, CAD, USD ou dans toute autre devise prise en charge, selon la devise sélectionnée par l&apos;utilisateur ou déterminée à partir de sa localisation. Les montants affichés sur le site ont une valeur informative. Le montant contractuel final est celui indiqué dans le devis, la confirmation de commande, le lien de paiement ou la facture émise.
              </p>
              <p className="mb-4">
                Le traitement fiscal dépend notamment du pays du client, de son statut de consommateur ou de professionnel, de la nature de la prestation, du lieu de fourniture de celle-ci et des règles fiscales applicables au moment de la facturation. Selon les cas, une facture peut être émise toutes taxes comprises, hors taxes, hors champ de la TVA suisse, ou avec mécanisme d&apos;autoliquidation lorsque le droit applicable le prévoit.
              </p>
              <p className="mb-4">
                Lorsque la TVA suisse est applicable, elle est facturée au taux légal en vigueur à la date de facturation. À titre informatif, le taux normal publié par l&apos;Administration fédérale des contributions est actuellement de 8,1 %. Lorsque des taxes, droits ou obligations déclaratives sont dus dans le pays du client, ils restent à la charge du client sauf mention contraire expresse.
              </p>
              <p>
                Le client professionnel s&apos;engage à communiquer des informations exactes et complètes sur son identité, son adresse de facturation et, le cas échéant, son numéro de TVA ou identifiant fiscal afin de permettre un traitement fiscal correct.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Paiement</h2>
              <p className="mb-4">
                Sauf disposition particulière contraire, les prestations sont payables comptant à la commande ou selon l&apos;échéancier convenu entre les parties. Un acompte peut être demandé avant le démarrage d&apos;une mission.
              </p>
              <p>
                Les solutions de paiement fractionné ou de financement proposées par des partenaires tiers, dont Klarna, sont optionnelles, soumises à disponibilité, à l&apos;éligibilité du client, au pays concerné et aux plafonds applicables au moment du paiement. Elles ne constituent jamais un droit acquis ni une modalité systématiquement offerte.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Délais et collaboration du client</h2>
              <p className="mb-4">
                Les délais annoncés sont indicatifs, sauf engagement exprès contraire. Ils commencent à courir à compter de la réception de l&apos;ensemble des éléments nécessaires au projet, y compris les validations, contenus, accès techniques, documents et acomptes éventuellement requis.
              </p>
              <p>
                Tout retard, silence, absence de validation ou transmission incomplète d&apos;informations par le client peut entraîner un report équivalent des délais de production ou de livraison.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Propriété intellectuelle</h2>
              <p className="mb-4">
                Sauf mention contraire écrite, les droits d&apos;utilisation sur les livrables réalisés pour le client sont transférés après paiement complet des sommes dues. Les outils, méthodes, composants réutilisables, savoir-faire, bibliothèques, modèles, automatisations génériques et éléments préexistants demeurent la propriété de Couteau Suisse Digital ou de leurs titulaires respectifs.
              </p>
              <p>
                Le client garantit disposer des droits nécessaires sur les contenus, marques, textes, images, bases de données et documents qu&apos;il transmet pour l&apos;exécution de la mission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Annulation et résiliation</h2>
              <p className="mb-4">
                En cas d&apos;annulation d&apos;une commande ou d&apos;arrêt anticipé d&apos;une mission, le client reste redevable des prestations déjà réalisées, des frais engagés et, le cas échéant, des livrables déjà produits ou réservés pour le projet.
              </p>
              <p>
                En cas de manquement grave par l&apos;une des parties à ses obligations contractuelles, l&apos;autre partie pourra résilier la relation après mise en demeure restée sans effet dans un délai raisonnable, sauf urgence ou impossibilité manifeste.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Responsabilité</h2>
              <p className="mb-4">
                {settings.brandFullName} est tenu à une obligation de moyens. Sa responsabilité ne pourra être engagée qu&apos;en cas de faute prouvée et uniquement pour les dommages directs résultant de l&apos;exécution de la prestation, à l&apos;exclusion des pertes indirectes, pertes de chiffre d&apos;affaires, pertes de données, pertes d&apos;exploitation ou atteintes à l&apos;image.
              </p>
              <p>
                En tout état de cause, sauf disposition impérative contraire, la responsabilité totale de {settings.brandFullName} au titre d&apos;une mission est limitée au montant effectivement payé par le client pour la prestation concernée.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Droit applicable et juridiction</h2>
              <p className="mb-4">
                Les présentes conditions générales sont régies par le droit suisse, sous réserve des dispositions impératives éventuellement applicables au bénéfice des consommateurs ou de toute autre règle impérative relevant du pays du client.
              </p>
              <p>
                Sauf disposition impérative contraire, tout litige relatif à l&apos;interprétation, la validité ou l&apos;exécution des présentes relève de la compétence des tribunaux du siège de {settings.legalCompanyName} en {settings.legalCountry}.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">10. Contact</h2>
              <p>
                Pour toute question juridique, commerciale ou fiscale liée à une commande, un devis ou une facture, vous pouvez nous contacter à l&apos;adresse <a href={`mailto:${settings.contactEmail}`} className="text-orange-500 hover:text-orange-600">{settings.contactEmail}</a>.
              </p>
            </section>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default Terms;
